"use client"

import type { LucideIcon } from "lucide-react"

import { cn } from "@/common/lib/utils"
import type { ClusterId } from "./cluster-map"

/**
 * In-page navigation for the parameter clusters.
 *
 * Roughly forty controls across ten cards is a wall: legible one card at a time,
 * unnavigable as a page. The rail restores two things the grid took away —
 * where am I, and what have I already changed.
 *
 * The count badge is the more useful half. It answers "what did that framework
 * actually touch?" without scrolling, and it is why `cluster-map.ts` exists.
 */

export interface RailItem {
    id: ClusterId
    label: string
    icon: LucideIcon
    changed: number
}

export default function SectionRail({
    items,
    active,
    onJump,
    heading,
}: {
    items: RailItem[]
    active: string | undefined
    onJump: (id: ClusterId) => void
    heading: string
}) {
    return (
        <nav
            aria-label={heading}
            className="sticky top-[calc(var(--lp-bar-height,72px)+1.5rem)] hidden self-start lg:block"
        >
            <p className="mb-2 px-2 text-xs font-medium text-muted-foreground">{heading}</p>
            <ul className="space-y-0.5 border-l">
                {items.map((item) => {
                    const isActive = item.id === active
                    return (
                        <li key={item.id}>
                            <button
                                type="button"
                                onClick={() => onJump(item.id)}
                                aria-current={isActive ? "true" : undefined}
                                className={cn(
                                    // The active marker is a border on the rail's own
                                    // line rather than a filled pill — a filled row at
                                    // this size competes with the cluster headers it
                                    // is pointing at.
                                    "-ml-px flex w-full items-center gap-2 border-l-2 py-1.5 pl-3 pr-2 text-left text-xs transition-colors",
                                    isActive
                                        ? "border-primary font-medium text-primary"
                                        : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
                                )}
                            >
                                <item.icon className="size-3.5 shrink-0" />
                                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                                {item.changed > 0 && (
                                    <span
                                        className="shrink-0 rounded-full bg-primary/10 px-1.5 text-xs font-semibold tabular-nums text-primary"
                                        // Announced rather than left as a bare number,
                                        // which reads as a count of items in the section.
                                        aria-label={`${item.changed}`}
                                    >
                                        {item.changed}
                                    </span>
                                )}
                            </button>
                        </li>
                    )
                })}
            </ul>
        </nav>
    )
}
